import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { HERO, IMAGES } from "@/data/content";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = ({ animateIn = true }) => {
  const reduceMotion = useReducedMotion();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <motion.img
          src={IMAGES.heroJacuzzi}
          alt="Jacuzzi na tarasie z widokiem na jezioro Juno"
          className="h-full w-full object-cover"
          initial={{ scale: 1 }}
          animate={reduceMotion ? { scale: 1 } : { scale: [1, 1.06, 1] }}
          transition={{ duration: 26, ease: "easeInOut", repeat: Infinity }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1c2419]/85 via-[#1c2419]/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1c2419]/70 via-transparent to-[#1c2419]/20" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 md:px-10">
        <div className="max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={animateIn ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#E8D9B5] backdrop-blur-sm"
          >
            <MapPin className="h-3.5 w-3.5" /> {HERO.eyebrow}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={animateIn ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 font-brand text-4xl leading-[1.05] text-white text-balance sm:text-5xl lg:text-7xl"
          >
            {HERO.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={animateIn ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg"
          >
            {HERO.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={animateIn ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#kontakt")}
              className="rounded-full bg-[#4A7055] px-8 py-4 text-sm font-semibold text-[#F9F7F3] shadow-xl shadow-black/20 transition-all hover:bg-[#3d5c47] hover:-translate-y-0.5"
            >
              Zarezerwuj termin
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#atrakcje")}
              className="rounded-full border border-white/40 bg-white/10 px-8 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/20"
            >
              Zobacz udogodnienia
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollTo("#o-domu")}
        initial={{ opacity: 0 }}
        animate={animateIn ? { opacity: 1 } : {}}
        transition={{ delay: 0.9 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/70 hover:text-white"
        aria-label="Przewiń w dół"
      >
        <ArrowDown className="h-6 w-6 animate-bounce" />
      </motion.button>
    </section>
  );
};
